import React, { createContext, useContext, useState, useEffect } from "react"
import { ConfigProvider, Grid, theme } from "antd"
import type { ThemeConfig } from "antd"
import {
  themeTokens,
  componentTokens,
  darkThemeTokens,
  darkComponentTokens,
} from "@mimber/ui-core"

// Theme types
export type Theme = "light" | "dark"

// Larger tap targets for Input/Select/Button/etc. below the `md` breakpoint
const MOBILE_CONTROL_HEIGHT = 44

// Light theme using tokens from ui-core
const lightTheme: ThemeConfig = {
  algorithm: theme.defaultAlgorithm,
  token: themeTokens,
  components: componentTokens,
  cssVar: { prefix: "ant" },
}

// Dark theme using tokens from ui-core
const darkTheme: ThemeConfig = {
  algorithm: theme.darkAlgorithm,
  token: darkThemeTokens,
  components: darkComponentTokens,
  cssVar: { prefix: "ant" },
}

// Get theme configuration based on theme mode, enlarging tap targets on mobile
function getThemeConfig(isDark: boolean, isMobile: boolean): ThemeConfig {
  const base = isDark ? darkTheme : lightTheme

  if (!isMobile) return base

  return {
    ...base,
    token: { ...base.token, controlHeight: MOBILE_CONTROL_HEIGHT },
    components: {
      ...base.components,
      Button: {
        ...base.components?.Button,
        controlHeight: MOBILE_CONTROL_HEIGHT,
      },
      Input: {
        ...base.components?.Input,
        controlHeight: MOBILE_CONTROL_HEIGHT,
      },
      Select: {
        ...base.components?.Select,
        controlHeight: MOBILE_CONTROL_HEIGHT,
      },
      InputNumber: {
        ...base.components?.InputNumber,
        controlHeight: MOBILE_CONTROL_HEIGHT,
      },
      DatePicker: {
        ...base.components?.DatePicker,
        controlHeight: MOBILE_CONTROL_HEIGHT,
      },
    },
  }
}

// Theme context value interface
interface ThemeContextValue {
  theme: Theme
  toggleTheme: () => void
  isDark: boolean
}

// Create theme context
const ThemeContext = createContext<ThemeContextValue | undefined>(undefined)

// Theme provider props interface
export interface ThemeProviderProps {
  children: React.ReactNode
  storageKey?: string
  defaultTheme?: Theme
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({
  children,
  storageKey = "mimber-theme",
  defaultTheme = "light",
}) => {
  // Initialize theme from localStorage or default
  const [currentTheme, setCurrentTheme] = useState<Theme>(() => {
    if (typeof window !== "undefined") {
      const savedTheme = localStorage.getItem(storageKey)
      return (savedTheme as Theme) || defaultTheme
    }
    return defaultTheme
  })

  // Save theme to localStorage whenever it changes
  useEffect(() => {
    if (typeof window !== "undefined") {
      localStorage.setItem(storageKey, currentTheme)

      // Update document class for global styling if needed
      document.documentElement.className = currentTheme
    }
  }, [currentTheme, storageKey])

  const toggleTheme = () => {
    setCurrentTheme((prev) => (prev === "light" ? "dark" : "light"))
  }

  const isDark = currentTheme === "dark"
  const screens = Grid.useBreakpoint()
  const isMobile = screens.md === false

  const contextValue: ThemeContextValue = {
    theme: currentTheme,
    toggleTheme,
    isDark,
  }

  return (
    <ThemeContext.Provider value={contextValue}>
      <ConfigProvider theme={getThemeConfig(isDark, isMobile)}>
        {children}
      </ConfigProvider>
    </ThemeContext.Provider>
  )
}

export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext)

  if (context === undefined) {
    throw new Error("useTheme must be used within a ThemeProvider")
  }

  return context
}

// Export theme configurations for external use if needed
export { lightTheme, darkTheme, getThemeConfig }
